import axios from 'axios';
import { getUserToken } from 'src/utils/Utils';

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_KESISWAAN || 'https://dev-api.siswamedia.com/student-point/api/v1/',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});
// Request interceptor for adding auth token and logging
axiosInstance.interceptors.request.use(
  (config) => {
    // Add auth token unless caller already set Authorization (e.g. static config API)
    const hasCustomAuth = config.headers?.Authorization || config.headers?.authorization;
    const token = getUserToken();
    if (token && !hasCustomAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request details in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        url: config.url,
        method: config.method,
        headers: config.headers,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and logging
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log error details
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: originalRequest?.url,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // You can add logic here to refresh the token
      // Example:
      // try {
      //   const newToken = await refreshAuthToken();
      //   originalRequest.headers.Authorization = `Bearer ${newToken}`;
      //   return axiosInstance(originalRequest);
      // } catch (refreshError) {
      //   // Redirect to login or dispatch logout action
      //   window.location.href = '/login';
      //   return Promise.reject(refreshError);
      // }
    }

    // Handle network errors
    if (!error.response) {
      error.message = 'Network error - please check your connection';
    }

    return Promise.reject(error);
  }
);

// Create RTK Query compatible base query
export const axiosBaseQuery = ({ baseUrl = '' } = {}) => async ({
  url,
  method = 'GET',
  body,
  params,
  headers = {},
  prepareHeaders,
  validateStatus,
  timeout,
  responseType,
  onUploadProgress,
  onDownloadProgress,
  signal, // AbortSignal for request cancellation
}) => {
  try {
    // Allow for dynamic header preparation
    let finalHeaders = { ...headers };
    if (prepareHeaders) {
      finalHeaders = prepareHeaders(finalHeaders) || finalHeaders;
    }

    // Handle FormData for file uploads
    if (body instanceof FormData) {
      delete finalHeaders['Content-Type']; // Let axios set the boundary
    }

    // Determine if URL is absolute (starts with http:// or https://)
    const isAbsoluteUrl = /^https?:\/\//.test(url);
    const finalUrl = isAbsoluteUrl ? url : baseUrl + url;

    const result = await axiosInstance({
      url: finalUrl,
      method,
      data: body,
      params,
      headers: finalHeaders,
      validateStatus: validateStatus || ((status) => status >= 200 && status < 300),
      timeout,
      responseType,
      onUploadProgress,
      onDownloadProgress,
      signal,
      // Override baseURL when using absolute URL to prevent double concatenation
      ...(isAbsoluteUrl ? { baseURL: '' } : {}),
    });

    return { data: result.data };
  } catch (axiosError) {
    let error = {
      status: axiosError.response?.status || 'FETCH_ERROR',
      data: axiosError.response?.data || axiosError.message,
    };

    // Handle different error scenarios
    if (!axiosError.response) {
      // Network error
      error = {
        status: 'FETCH_ERROR',
        error: axiosError.message || 'Network error occurred',
      };
    } else if (axiosError.response.status === 401) {
      // Unauthorized
      error = {
        status: 401,
        data: axiosError.response.data || { message: 'Unauthorized access' },
      };
    } else if (axiosError.response.status === 403) {
      // Forbidden
      error = {
        status: 403,
        data: axiosError.response.data || { message: 'Access forbidden' },
      };
    } else if (axiosError.response.status === 404) {
      // Not found
      error = {
        status: 404,
        data: axiosError.response.data || { message: 'Resource not found' },
      };
    } else if (axiosError.response.status >= 500) {
      // Server error
      error = {
        status: axiosError.response.status,
        data: axiosError.response.data || { message: 'Server error occurred' },
      };
    }

    // Handle request cancellation
    if (axiosError.code === 'ERR_CANCELED') {
      error = {
        status: 'CANCELLED',
        error: 'Request was cancelled',
      };
    }

    // Handle timeout
    if (axiosError.code === 'ECONNABORTED') {
      error = {
        status: 'TIMEOUT',
        error: 'Request timeout',
      };
    }

    return { error };
  }
};

// Export the axios instance for direct use when needed
export { axiosInstance };

// Utility function to create a base query with custom configuration
export const createAxiosBaseQuery = (customConfig = {}) => {
  const mergedConfig = {
    baseUrl: '',
    ...customConfig,
  };

  return axiosBaseQuery(mergedConfig);
};

// Helper function for file upload with progress tracking
export const uploadFile = async (url, formData, onProgress) => {
  try {
    const response = await axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });

    return { data: response.data };
  } catch (error) {
    return {
      error: {
        status: error.response?.status || 'UPLOAD_ERROR',
        data: error.response?.data || error.message,
      },
    };
  }
};

// Helper function to download files
export const downloadFile = async (url, filename) => {
  try {
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
    });

    // Create a download link
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename || 'download');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return { data: { success: true, filename } };
  } catch (error) {
    return {
      error: {
        status: error.response?.status || 'DOWNLOAD_ERROR',
        data: error.response?.data || error.message,
      },
    };
  }
};

export default axiosBaseQuery;
